/* eslint-disable no-unused-vars */
import InfoIcon from "@mui/icons-material/Info";
import {
	Box,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import TooltipContainer from "../TooltipContainer";
import ControlledCheckbox from "../controlled/ControlledCheckbox";
import ControlledRadioGroup from "../controlled/ControlledRadioGroup";
import ControlledSelect from "../controlled/ControlledSelect";

import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../features/auth/AuthContext";
import { useGetIntegrationStatusQuery } from "../../features/integrations/integrationsApiSlice";
import { useGetCurrentUserMailingGroupsQuery } from "../../features/recipients/recipientsApiSlice";
import { CargoFormSms } from "../../features/sms/cargo-form";

export const SalesPart = ({
	control,
	errors,
	settings,
	resetField,
	setValue,
	getValues,
	watch,
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const hasCustomTransConnection = user?.hasCustomTransConnection;

	const {
		data: mailingGroups,
		error: mailingGroupsError,
		isLoading: isLoadingGroups,
	} = useGetCurrentUserMailingGroupsQuery();

	const {
		data: integrationsStatuses,
		error: integrationsError,
		isLoading: isLoadingIntegrations,
	} = useGetIntegrationStatusQuery();

	const mailingGroupOptions = useMemo(() => {
		if (!mailingGroups?.groups) {
			return [];
		}

		return Object.entries(mailingGroups?.groups).map(([key, name]) => ({
			value: key,
			label: name,
		}));
	}, [mailingGroups?.groups]);

	const handleUncheckAllStocks = () => {
		const isChecked = getValues("shouldPostOnStock");

		if (isChecked) {
			setValue("postOnTrans", true);
			setValue("postOnTimocom", integrationsStatuses.timocomStatus);
			setValue("postOnTeleroute", integrationsStatuses.telerouteStatus);
			setValue(
				"shouldPostAfter",
				integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus
					? "0"
					: null
			);
			setValue("gatherPriceProposals", integrationsStatuses.timocomStatus);
			setValue(
				"shouldRefreshOffer",
				integrationsStatuses.transEuStatus ||
					integrationsStatuses.timocomStatus ||
					integrationsStatuses.telerouteStatus
			);
		} else {
			setValue("postOnTrans", false);
			setValue("postOnTimocom", false);
			setValue("postOnTeleroute", false);
			setValue("shouldPostAfter", null);
			setValue("gatherPriceProposals", false);
			setValue("shouldRefreshOffer", false);
		}
	};

	const watchCargoTypeId = watch("cargoTypeId");
	const isCheckedPostOnTrans = watch("postOnTrans");
	const [showAdditionalField, setShowAdditionalField] = useState(false);
	useEffect(() => {
		if (
			hasCustomTransConnection &&
			(isCheckedPostOnTrans || integrationsStatuses?.transEuStatus)
		) {
			setShowAdditionalField(true);
			if (!watchCargoTypeId) {
				setValue("cargoTypeId", "PALETTE");
			}
		}
	}, [integrationsStatuses, isLoadingIntegrations]);

	useEffect(() => {
		if (hasCustomTransConnection && integrationsStatuses?.transEuStatus) {
			if (isCheckedPostOnTrans === false) {
				setShowAdditionalField(false);
				setValue("cargoTypeId", null);
				return;
			}
			if (isCheckedPostOnTrans === true) {
				setShowAdditionalField(true);
				if (!watchCargoTypeId) {
					setValue("cargoTypeId", "PALETTE");
				}
				return;
			}
		}
	}, [isCheckedPostOnTrans]);

	return (
		<Grid container rowSpacing={1.5} columnSpacing={1} sx={{ py: 1, px: 2 }}>
			<Grid
				container
				rowSpacing={0.5}
				columnSpacing={1}
				size={{
					lg: 12,
				}}
			>
				<Grid
					size={{
						lg: 12,
					}}
				>
					<Box sx={{ mt: "9px" }}>
						<ControlledCheckbox
							control={control}
							margin="normal"
							name="mailingToGroups"
							label={
								<span>
									{t("mailing_to_trusted_and_gathering_offers")}{" "}
									<Link
										style={{
											marginLeft: "20px",
											color: "#0070C0",
										}}
										to="/recipients"
									>
										{t("manage_groups")}
									</Link>
								</span>
							}
						/>
					</Box>
				</Grid>
				<Grid
					container
					rowSpacing={0}
					columnSpacing={1}
					sx={{ ml: "20px" }}
					size={{
						lg: 11,
					}}
				>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_1"
									label={t("group_select", { number: 1 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_2"
									label={t("group_select", { number: 2 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_3"
									label={t("group_select", { number: 3 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
				</Grid>
			</Grid>
			{isLoadingIntegrations ? (
				<Grid
					rowSpacing={0.5}
					columnSpacing={1}
					size={{
						lg: 12,
					}}
				>
					<Skeleton variant="rectangular" height={122.5} />
				</Grid>
			) : (
				<>
					<Grid
						container
						rowSpacing={0.5}
						columnSpacing={1}
						size={{
							lg: 12,
						}}
					>
						<Grid
							size={{
								lg: 12,
							}}
						>
							<Box sx={{ display: "flex" }}>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="shouldPostOnStock"
									label={<span>{t("publish_on_stocks")}</span>}
									defaultValue={
										integrationsStatuses.transEuStatus ||
										integrationsStatuses.timocomStatus ||
										integrationsStatuses.telerouteStatus ||
										true
									}
									disabled={
										!integrationsStatuses.transEuStatus &&
										!integrationsStatuses.timocomStatus &&
										!integrationsStatuses.telerouteStatus &&
										false
									}
									onChange={handleUncheckAllStocks}
								/>
								<Box sx={{ display: "flex" }}>
									<ControlledCheckbox
										control={control}
										margin="normal"
										name="postOnTrans"
										label={<span>Trans</span>}
										defaultValue={
											hasCustomTransConnection
												? integrationsStatuses.transEuStatus
												: true
										}
										disabled={
											hasCustomTransConnection
												? !integrationsStatuses.transEuStatus
												: false
										}
										onChange={() =>
											setValue("shouldPostOnStock", true, {
												shouldValidate: true,
											})
										}
										error={!!errors?.postOnTrans}
									/>
									<ControlledCheckbox
										control={control}
										margin="normal"
										name="postOnTimocom"
										label={<span>Timocom</span>}
										defaultValue={integrationsStatuses.timocomStatus}
										disabled={!integrationsStatuses.timocomStatus}
										onChange={() =>
											setValue("shouldPostOnStock", true, {
												shouldValidate: true,
											})
										}
										error={!!errors?.postOnTimocom}
									/>
									<ControlledCheckbox
										control={control}
										margin="normal"
										name="postOnTeleroute"
										label={<span>Teleroute</span>}
										defaultValue={integrationsStatuses.telerouteStatus}
										disabled={!integrationsStatuses.telerouteStatus}
										onChange={() =>
											setValue("shouldPostOnStock", true, {
												shouldValidate: true,
											})
										}
										error={!!errors?.postOnTeleroute}
									/>
								</Box>
								{showAdditionalField && (
									<Box sx={{ flex: "1" }}>
										<ControlledSelect
											control={control}
											margin="none"
											name="cargoTypeId"
											label={t("cargo_type_label") + " *"}
											defaultValue=""
											error={!!errors?.cargoTypeId}
											onChange={() => {}}
											options={Object.entries(
												settings.vehicleOptions.cargoTypesOptions
											).map(([value, label]) => {
												return { value, label };
											})}
											sx={{ maxWidth: "130px" }}
										/>
									</Box>
								)}
							</Box>
						</Grid>
						<Grid
							sx={{ ml: "28px" }}
							size={{
								lg: 11,
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "20px",
								}}
							>
								<ControlledRadioGroup
									className="radio-post-after"
									control={control}
									margin="normal"
									name="shouldPostAfter"
									label={t("publish_when")}
									defaultValue={
										integrationsStatuses.transEuStatus ||
										integrationsStatuses.timocomStatus ||
										integrationsStatuses.telerouteStatus
											? "0"
											: null
									}
									disabled={
										!integrationsStatuses.transEuStatus &&
										!integrationsStatuses.timocomStatus &&
										!integrationsStatuses.telerouteStatus
									}
									options={[
										{ value: 0, label: t("right_away") },
										{ value: 10, label: t("after_10_min") },
										{ value: 20, label: t("after_20_min") },
									]}
								/>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
									}}
								>
									<Controller
										control={control}
										name="shouldRefreshOffer"
										defaultValue={
											integrationsStatuses.transEuStatus ||
											integrationsStatuses.timocomStatus ||
											integrationsStatuses.telerouteStatus
										}
										render={({ field }) => {
											return (
												<Box sx={{ position: "relative" }}>
													<RadioGroup
														className="radio-post-after"
														{...field}
														value={
															field.value === true
																? "true"
																: field.value === false
																? "false"
																: "notExisting"
														}
														onChange={(e) =>
															field.onChange(e.target.value === "true")
														}
													>
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																gap: "4px",
															}}
														>
															<FormControlLabel
																value="true"
																control={<Radio />}
																label={
																	<span style={{ whiteSpace: "nowrap" }}>
																		{t("refresh_offer_after", { value: 35 })}
																	</span>
																}
																disabled={
																	!integrationsStatuses.transEuStatus &&
																	!integrationsStatuses.timocomStatus &&
																	!integrationsStatuses.telerouteStatus
																}
															/>
															<Tooltip
																title={
																	<TooltipContainer>
																		<p style={{ margin: 0 }}>
																			{t(
																				"refreshing_offer_will_be_active_until"
																			)}
																		</p>
																	</TooltipContainer>
																}
																placement="top-start"
																slotProps={{
																	popper: {
																		sx: {
																			"& > div": {
																				background: "#fff",
																				p: 0,
																				fontSize: "11px",
																			},
																		},
																	},
																}}
															>
																<InfoIcon sx={{ color: "#F07C00" }} />
															</Tooltip>
														</Box>
														<Box
															sx={{
																position: "absolute",
																bottom: "-30px",
																left: "0",
															}}
														>
															<FormControlLabel
																sx={{ width: "149.7px" }}
																value="false"
																control={<Radio />}
																label={t("refresh_manually")}
																disabled={
																	!integrationsStatuses.transEuStatus &&
																	!integrationsStatuses.timocomStatus &&
																	!integrationsStatuses.telerouteStatus
																}
															/>
														</Box>
													</RadioGroup>
												</Box>
											);
										}}
									/>
								</Box>
							</Box>
						</Grid>
					</Grid>
					<Grid
						sx={{ ml: "28px" }}
						size={{
							lg: 11,
						}}
					>
						<ControlledCheckbox
							className="button-checkbox"
							control={control}
							margin="normal"
							error={!!errors?.gatherPriceProposals}
							name="gatherPriceProposals"
							defaultValue={integrationsStatuses.timocomStatus}
							disabled={!integrationsStatuses.timocomStatus}
							label={<span>{t("gather_timocom_price_proposals")}</span>}
						/>
					</Grid>
					<Grid
						size={{
							lg: 12,
						}}
					>
						<Box sx={{ mb: "4px" }}>
							<ControlledCheckbox
								control={control}
								margin="normal"
								error={!!errors?.autoSearchFreeCars}
								name="autoSearchFreeCars"
								defaultValue={integrationsStatuses.timocomStatus}
								disabled={!integrationsStatuses.timocomStatus}
								label={
									<span>
										{t("auto_search_free_cars_timocom")}{" "}
										<span style={{ fontSize: "10px" }}>
											({t("radius_kilometers", { value: 100 })})
										</span>
									</span>
								}
							/>
							<Tooltip
								title={
									<TooltipContainer>
										<p style={{ margin: 0 }}>
											{t("timocom_how_to_activate_tooltip_1")}
										</p>
										<p style={{ margin: 0 }}>
											{t("timocom_how_to_activate_tooltip_2")}
										</p>
									</TooltipContainer>
								}
								placement="top-start"
								slotProps={{
									popper: {
										sx: {
											"& > div": {
												background: "#fff",
												p: 0,
												fontSize: "11px",
											},
										},
									},
								}}
							>
								<Typography
									sx={{
										fontSize: "12px",
										display: "inline",
										color: "#0070C0",
										textDecoration: "underline",
										cursor: "pointer",
									}}
								>
									{t("how_to_activate")}
								</Typography>
							</Tooltip>
						</Box>
					</Grid>
					<Grid item lg={12}>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<ControlledCheckbox
								control={control}
								margin="normal"
								error={!!errors?.shouldBePublic}
								name="shouldBePublic"
								label={<span>{t("whatsapp_add_to_table")}</span>}
							/>
							<Tooltip
								PopperProps={{
									sx: {
										"& > div": {
											background: "#fff",
											p: 0,
											fontSize: "11px",
										},
									},
								}}
								title={
									<TooltipContainer>
										<p style={{ margin: 0 }}>{t("whatsapp_info_tooltip")}</p>
									</TooltipContainer>
								}
								placement="top-start"
							>
								<InfoIcon sx={{ color: "#F07C00" }} />
							</Tooltip>
						</Box>
					</Grid>
					<Grid
						item
						size={{
							lg: 12,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<ControlledCheckbox
									control={control}
									margin="normal"
									error={!!errors?.sendSMS}
									name="sendSMS"
									label={<span>Wyślij SMS</span>}
								/>
								<Tooltip
									PopperProps={{
										sx: {
											"& > div": {
												background: "#fff",
												p: 0,
												fontSize: "11px",
											},
										},
									}}
									title={
										<TooltipContainer>
											<p style={{ margin: 0 }}>Opis tutaj</p>
										</TooltipContainer>
									}
									placement="top-start"
								>
									<InfoIcon sx={{ color: "#F07C00" }} />
								</Tooltip>
							</Box>
							<CargoFormSms control={control} />
						</Box>
					</Grid>
				</>
			)}
		</Grid>
	);
};
